import React, { useState, useEffect } from 'react';
import { X, Table, Plus, Save, RefreshCw, CheckCircle2, AlertTriangle, Info, Users, Shield, FileText, Check, Calendar } from 'lucide-react';
import {
  SafetyIndicator,
  TargetDirection,
  ThresholdMethodology,
} from '../types';
import { performanceService } from '../performanceService';
import { administrationService, Department } from '../../../administration/administrationService';
import { AppDialogModal, NotificationType } from '../../../../components/AppDialogModal';
import { formatUserFriendlyError } from '../../../../shared/errorUtils';

interface SPIDetailModalProps {
  indicator: SafetyIndicator | null;
  onClose: () => void;
  onRefresh: () => void;
  isSmsUser?: boolean;
  currentUserDept?: string;
  selectedYear?: number;
}

interface GridRow {
  period: string; // YYYY-MM
  numerator: number | string;
  denominator: number | string;
  notes: string;
  status: string;
  isNew?: boolean;
  isDirty?: boolean;
}

export const SPIDetailModal: React.FC<SPIDetailModalProps> = ({
  indicator,
  onClose,
  onRefresh,
  isSmsUser = false,
  currentUserDept,
  selectedYear = new Date().getFullYear(),
}) => {
  const [activeTab, setActiveTab] = useState<'VALUES' | 'GRID' | 'CONFIG' | 'ACTIONS_RISKS'>('GRID');
  const [loading, setLoading] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);
  const [indicatorDetail, setIndicatorDetail] = useState<SafetyIndicator | null>(indicator);
  const [gridYear, setGridYear] = useState<number>(selectedYear);

  // Department, Roles, and Frequency (Item 6 & Items 11, 13, 14)
  const [deptList, setDeptList] = useState<Department[]>([]);
  const [editOwnerDept, setEditOwnerDept] = useState<string>('');
  const [editProviderDept, setEditProviderDept] = useState<string>('');
  const [editProviderInstructions, setEditProviderInstructions] = useState<string>('');
  const [editFrequency, setEditFrequency] = useState<string>(indicator?.frequency || 'MONTHLY');
  const [editStakeholders, setEditStakeholders] = useState<string[]>(indicator?.stakeholderDepartments || []);
  const [editMethodology, setEditMethodology] = useState<ThresholdMethodology>(indicator?.thresholdMethodology || 'THREE_SIGMA');
  const [editAmberVal, setEditAmberVal] = useState<number | ''>(indicator?.targetAmberValue ?? '');
  const [editRedVal, setEditRedVal] = useState<number | ''>(indicator?.targetRedValue ?? '');
  const [savingRoles, setSavingRoles] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (indicatorDetail) {
      setEditStakeholders(indicatorDetail.stakeholderDepartments || []);
      setEditMethodology(indicatorDetail.thresholdMethodology || 'THREE_SIGMA');
      setEditAmberVal(indicatorDetail.targetAmberValue ?? '');
      setEditRedVal(indicatorDetail.targetRedValue ?? '');
    }
  }, [indicatorDetail]);

  const ownerDept = indicatorDetail?.ownerDepartment || indicatorDetail?.department || 'Unassigned';
  const providerDept = indicatorDetail?.dataProviderDepartment || ownerDept;

  const normUserDept = currentUserDept?.trim().toLowerCase();
  const isOwner = Boolean(normUserDept) && normUserDept === ownerDept.trim().toLowerCase();
  const isDataProvider = Boolean(normUserDept) && normUserDept === providerDept.trim().toLowerCase();
  const stakeholderDepts = (indicatorDetail?.stakeholderDepartments || []).map((d) => d.trim().toLowerCase());
  const isStakeholder = Boolean(normUserDept) && stakeholderDepts.includes(normUserDept) && !isOwner && !isDataProvider && !isSmsUser;

  // A user can enter/edit values if they are SMS personnel, OR the Data Provider, OR the SPI Owner!
  // Stakeholder departments can only view the indicator and its alert level (Item 11)!
  const canEditIndicator = !isStakeholder && (isSmsUser || isDataProvider || isOwner);

  // SPI Owner and SMS personnel can define Actions and Risks for this SPI!
  const canDefineActionRisk = !isStakeholder && (isSmsUser || isOwner);

  // Linked Actions and Risks State (Item 4)
  const [linkedActions, setLinkedActions] = useState<any[]>([]);
  const [linkedRisks, setLinkedRisks] = useState<any[]>([]);
  const [loadingActionsRisks, setLoadingActionsRisks] = useState<boolean>(false);
  const [showAddActionModal, setShowAddActionModal] = useState<boolean>(false);
  const [showAddRiskModal, setShowAddRiskModal] = useState<boolean>(false);

  // New Action Form State
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionType, setNewActionType] = useState('CORRECTIVE');
  const [newActionPriority, setNewActionPriority] = useState('MEDIUM');
  const [newActionAssignee, setNewActionAssignee] = useState('');
  const [newActionDepartment, setNewActionDepartment] = useState('');
  const [newActionDueDate, setNewActionDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [newActionDescription, setNewActionDescription] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  // New Risk Form State
  const [newRiskTitle, setNewRiskTitle] = useState('');
  const [newRiskSeverity, setNewRiskSeverity] = useState<number>(3);
  const [newRiskLikelihood, setNewRiskLikelihood] = useState<string>('C');
  const [newRiskDepartment, setNewRiskDepartment] = useState('');
  const [newRiskDescription, setNewRiskDescription] = useState('');
  const [newRiskJustification, setNewRiskJustification] = useState('');
  const [submittingRisk, setSubmittingRisk] = useState(false);

  // Notification Dialog State
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    type: NotificationType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showNotification = (title: string, message: string, type: NotificationType = 'info') => {
    setDialogState({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  // Config Form State (Target & Threshold)
  const [targetYear, setTargetYear] = useState<number>(2026);
  const [targetValue, setTargetValue] = useState<number>(indicator?.target?.targetValue ?? 0);
  const [targetDirection, setTargetDirection] = useState<TargetDirection>(
    indicator?.target?.targetDirection ?? 'BELOW'
  );
  const [baselinePeriod, setBaselinePeriod] = useState<string>(
    indicator?.threshold?.baselinePeriod ?? '2024-2025'
  );
  const [baselineMean, setBaselineMean] = useState<number>(indicator?.threshold?.baselineMean ?? 1.0);
  const [baselineStdDev, setBaselineStdDev] = useState<number>(indicator?.threshold?.baselineStdDev ?? 0.2);
  const [alert1Value, setAlert1Value] = useState<number>(indicator?.threshold?.alert1Value ?? 1.2);
  const [alert2Value, setAlert2Value] = useState<number>(indicator?.threshold?.alert2Value ?? 1.4);
  const [alert3Value, setAlert3Value] = useState<number>(indicator?.threshold?.alert3Value ?? 1.6);

  // Excel Grid Table State
  const [gridRows, setGridRows] = useState<GridRow[]>([]);
  
  useEffect(() => {
    administrationService.getDepartments().then((depts) => {
      setDeptList(depts.filter((d) => d.status === 'ACTIVE'));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (indicator?.id) {
      loadDetails(indicator.id, gridYear);
    }
  }, [indicator?.id, gridYear]);

  const getNormalizedFrequency = (freqVal?: string): 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' => {
    const raw = (freqVal || editFrequency || indicatorDetail?.frequency || indicator?.frequency || 'MONTHLY').toUpperCase().trim();
    if (raw === 'QUARTERLY') return 'QUARTERLY';
    if (raw === 'ANNUAL' || raw === 'ANNUALLY') return 'ANNUAL';
    return 'MONTHLY';
  };

  const getPeriodDisplayInfo = (period: string, freq: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL') => {
    if (freq === 'QUARTERLY') {
      const match = period.match(/^(\d{4})-Q([1-4])$/i);
      if (match) {
        const qNames: Record<string, string> = {
          '1': 'Q1 (Jan - Mar)',
          '2': 'Q2 (Apr - Jun)',
          '3': 'Q3 (Jul - Sep)',
          '4': 'Q4 (Oct - Dec)',
        };
        return {
          title: `${qNames[match[2]] || `Quarter ${match[2]}`}`,
          subtitle: `Quarter ${match[2]} ${match[1]} (${period})`,
          code: period,
        };
      }
    } else if (freq === 'ANNUAL') {
      return {
        title: `Full Year ${period}`,
        subtitle: `Annual Total (${period})`,
        code: period,
      };
    } else {
      const match = period.match(/^(\d{4})-(\d{2})$/);
      if (match) {
        const monthNames = [
          'Ocak (January)', 'Şubat (February)', 'Mart (March)', 'Nisan (April)', 'Mayıs (May)', 'Haziran (June)',
          'Temmuz (July)', 'Ağustos (August)', 'Eylül (September)', 'Ekim (October)', 'Kasım (November)', 'Aralık (December)',
        ];
        const mIdx = parseInt(match[2], 10) - 1;
        return {
          title: `${monthNames[mIdx] || match[2]} ${match[1]}`,
          subtitle: `Month ${match[2]} (${period})`,
          code: period,
        };
      }
    }
    return {
      title: period,
      subtitle: period,
      code: period,
    };
  };

  const generatePeriodRange = (
    freq: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' = getNormalizedFrequency(),
    yr: number = gridYear,
    existingPeriods: string[] = []
  ): string[] => {
    const periods: string[] = [];

    if (freq === 'QUARTERLY') {
      for (let q = 1; q <= 4; q++) {
        periods.push(`${yr}-Q${q}`);
      }
    } else if (freq === 'ANNUAL') {
      periods.push(`${yr}`);
    } else {
      for (let m = 1; m <= 12; m++) {
        const mStr = m < 10 ? `0${m}` : `${m}`;
        periods.push(`${yr}-${mStr}`);
      }
    }

    // Include existing periods in determination of min start
    for (const p of existingPeriods) {
      if ((p.startsWith(`${yr}-`) || p === `${yr}`) && !periods.includes(p)) {
        periods.push(p);
      }
    }

    periods.sort();
    return periods;
  };

  const syncGridRowsFromData = (data: SafetyIndicator, yr: number = gridYear) => {
    const rawValues = data.recentValues && data.recentValues.length > 0
      ? data.recentValues.map((rv: any) => ({
          period: rv.period,
          numerator: rv.numeratorValue ?? rv.numerator ?? 0,
          denominator: rv.denominatorValue ?? rv.denominator ?? 1000,
          status: rv.status ?? 'LOCKED',
          notes: rv.notes || '',
        }))
      : (data.history || []).map((rv: any) => ({
          period: rv.period,
          numerator: rv.numeratorValue ?? rv.numerator ?? 0,
          denominator: rv.denominatorValue ?? rv.denominator ?? 1000,
          status: rv.status ?? 'LOCKED',
          notes: rv.notes || '',
        }));

    const valueMap = new Map<string, any>();
    for (const v of rawValues) {
      if (v.period && (v.period.startsWith(`${yr}-`) || v.period === `${yr}`)) {
        valueMap.set(v.period, v);
      }
    }

    const freq = getNormalizedFrequency(data.frequency);
    const allPeriods = generatePeriodRange(freq, yr, Array.from(valueMap.keys()));

    const rows: GridRow[] = allPeriods.map((p) => {
      const existing = valueMap.get(p);
      if (existing) {
        return {
          period: p,
          numerator: existing.numerator ?? 0,
          denominator: existing.denominator ?? 1000,
          notes: existing.notes || '',
          status: existing.status || 'LOCKED',
          isDirty: false,
        };
      }
      return {
        period: p,
        numerator: '',
        denominator: 1000,
        notes: '',
        status: 'OPEN',
        isNew: true,
        isDirty: false,
      };
    });

    setGridRows(rows);
  };

  const loadDetails = async (id: string, yr: number = gridYear) => {
    try {
      setLoading(true);
      const data = await performanceService.getIndicatorById(id, yr);
      setIndicatorDetail(data);

      if (data.target) {
        setTargetYear(data.target.targetYear);
        setTargetValue(data.target.targetValue);
        setTargetDirection(data.target.targetDirection);
      }
      if (data.threshold) {
        setBaselinePeriod(data.threshold.baselinePeriod);
        setBaselineMean(data.threshold.baselineMean);
        setBaselineStdDev(data.threshold.baselineStdDev);
        setAlert1Value(data.threshold.alert1Value);
        setAlert2Value(data.threshold.alert2Value);
        setAlert3Value(data.threshold.alert3Value);
      }

      setEditOwnerDept(data.ownerDepartment || data.department || '');
      setEditProviderDept(data.dataProviderDepartment || data.ownerDepartment || data.department || '');
      setEditProviderInstructions(data.dataProviderInstructions || '');
      setEditFrequency(data.frequency || 'MONTHLY');

      syncGridRowsFromData(data, yr);
      loadActionsAndRisks(id);
    } catch (err) {
      console.error('Error fetching indicator details:', err);
      showNotification('Data Load Error', formatUserFriendlyError(err), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadActionsAndRisks = async (indId?: string) => {
    const id = indId || indicatorDetail?.id;
    if (!id) return;
    try {
      setLoadingActionsRisks(true);
      const [actions, risks] = await Promise.all([
        performanceService.getIndicatorActions(id),
        performanceService.getIndicatorRisks(id),
      ]);
      setLinkedActions(actions || []);
      setLinkedRisks(risks || []);
    } catch (err) {
      console.error('Failed to load linked actions/risks:', err);
    } finally {
      setLoadingActionsRisks(false);
    }
  };

  const handleCreateAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!indicatorDetail) return;
    if (!newActionTitle.trim()) {
      showNotification('Validation Error', 'Action title is required.', 'warning');
      return;
    }
    try {
      setSubmittingAction(true);
      await performanceService.createIndicatorAction(indicatorDetail.id, {
        title: newActionTitle.trim(),
        description: newActionDescription.trim() || undefined,
        actionType: newActionType,
        priority: newActionPriority,
        department: newActionDepartment || ownerDept,
        assigneeName: newActionAssignee.trim() || undefined,
        dueDate: newActionDueDate,
      });
      showNotification('Safety Action Created', 'New Safety Action (CAPA) linked to this SPI has been registered.', 'success');
      setShowAddActionModal(false);
      setNewActionTitle('');
      setNewActionDescription('');
      setNewActionAssignee('');
      await loadActionsAndRisks(indicatorDetail.id);
    } catch (err) {
      showNotification('Action Creation Failed', formatUserFriendlyError(err), 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleCreateRisk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!indicatorDetail) return;
    if (!newRiskTitle.trim()) {
      showNotification('Validation Error', 'Risk title is required.', 'warning');
      return;
    }
    try {
      setSubmittingRisk(true);
      await performanceService.createIndicatorRisk(indicatorDetail.id, {
        title: newRiskTitle.trim(),
        description: newRiskDescription.trim() || undefined,
        initialSeverity: Number(newRiskSeverity),
        initialLikelihood: newRiskLikelihood,
        department: newRiskDepartment || ownerDept,
        justification: newRiskJustification.trim() || undefined,
      });
      showNotification('Safety Risk Identified', 'New Safety Risk linked to this SPI has been registered.', 'success');
      setShowAddRiskModal(false);
      setNewRiskTitle('');
      setNewRiskDescription('');
      setNewRiskJustification('');
      await loadActionsAndRisks(indicatorDetail.id);
    } catch (err) {
      showNotification('Risk Creation Failed', formatUserFriendlyError(err), 'error');
    } finally {
      setSubmittingRisk(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!indicatorDetail) return;
    const isArchived = indicatorDetail.status === 'ARCHIVED';
    const actionName = isArchived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${actionName} indicator ${indicatorDetail.code}?`)) {
      return;
    }
    try {
      setArchiving(true);
      if (isArchived) {
        const updated = await performanceService.unarchiveIndicator(indicatorDetail.id);
        setIndicatorDetail(updated);
        showNotification('SPI Restored', `Indicator ${indicatorDetail.code} is now active.`, 'success');
      } else {
        const updated = await performanceService.archiveIndicator(indicatorDetail.id);
        setIndicatorDetail(updated);
        showNotification('SPI Archived', `Indicator ${indicatorDetail.code} has been moved to archive.`, 'success');
      }
      onRefresh();
    } catch (err: any) {
      showNotification(`Failed to ${actionName} indicator`, formatUserFriendlyError(err), 'error');
    } finally {
      setArchiving(false);
    }
  };

  const handleSaveRoles = async () => {
    if (!indicatorDetail) return;
    try {
      setSavingRoles(true);
      await performanceService.updateIndicatorRoles(indicatorDetail.id, {
        ownerDepartment: editOwnerDept,
        dataProviderDepartment: editProviderDept,
        dataProviderInstructions: editProviderInstructions,
        frequency: editFrequency,
        stakeholderDepartments: editStakeholders,
        thresholdMethodology: editMethodology,
        targetAmberValue: editAmberVal !== '' ? Number(editAmberVal) : undefined,
        targetRedValue: editRedVal !== '' ? Number(editRedVal) : undefined,
      });
      await loadDetails(indicatorDetail.id, gridYear);
      onRefresh();
      showNotification('Success', 'SPI Ownership, Stakeholders, Methodology, and Frequency updated successfully.', 'success');
    } catch (err) {
      showNotification('Role Update Error', formatUserFriendlyError(err), 'error');
    } finally {
      setSavingRoles(false);
    }
  };

  const handleGridCellChange = (
    index: number,
    field: 'numerator' | 'denominator' | 'notes',
    value: string
  ) => {
    setGridRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], isDirty: true };

      if (field === 'numerator' || field === 'denominator') {
        row[field] = value;
      } else {
        row.notes = value;
      }

      updated[index] = row;
      return updated;
    });
  };

  
  const handleSaveAllGrid = async () => {
    if (!indicatorDetail) return;

    // Filter rows that are dirty or newly entered with non-empty numerator
    const rowsToSave = gridRows.filter((r) => r.isDirty && r.numerator !== '');

    if (rowsToSave.length === 0) {
      showNotification('Save Info', 'No modified or new records found in the table to save.', 'info');
      return;
    }

    try {
      setSavingBatch(true);
      const payload = rowsToSave.map((r) => ({
        period: r.period,
        numerator: Number(r.numerator || 0),
        denominator: Number(r.denominator || 1),
        notes: r.notes || '',
        recordedBy: 'SAFETY_OFFICER',
      }));

      await performanceService.recordIndicatorValuesBatch(indicatorDetail.id, payload);
      await loadDetails(indicatorDetail.id, gridYear);
      onRefresh();
      showNotification(
        'Success',
        `Safety data for ${rowsToSave.length} period(s) successfully saved and 3-Sigma threshold values recalculated.`,
        'success'
      );
    } catch (err) {
      showNotification('Save Error', formatUserFriendlyError(err), 'error');
    } finally {
      setSavingBatch(false);
    }
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!indicatorDetail) return;
    try {
      setLoading(true);
      await performanceService.setTarget(indicatorDetail.id, {
        targetYear: Number(targetYear),
        targetValue: Number(targetValue),
        targetDirection,
        unit: indicatorDetail.unit || 'PER_1000_FLIGHTS',
        effectiveFrom: `${targetYear}-01-01`,
      });
      await performanceService.setThreshold(indicatorDetail.id, {
        baselinePeriod,
        baselineMean: Number(baselineMean),
        baselineStdDev: Number(baselineStdDev),
        alert1Value: Number(alert1Value),
        alert2Value: Number(alert2Value),
        alert3Value: Number(alert3Value),
      });
      await loadDetails(indicatorDetail.id, gridYear);
      onRefresh();
      showNotification('Configuration Saved', 'SPI target and 3-Sigma statistical threshold limits successfully updated.', 'success');
      setActiveTab('GRID');
    } catch (err) {
      showNotification('Save Error', formatUserFriendlyError(err), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper to calculate rate and alert level live in grid row
  const computeRowRate = (num: number | string, den: number | string): number | null => {
    if (num === '' || den === '') return null;
    const n = Number(num);
    const d = Number(den);
    if (isNaN(n) || isNaN(d) || d === 0) return 0;
    return Math.round(((n / d) * 1000) * 100) / 100;
  };

  const evaluateAlert = (rate: number | null): { text: string; badgeClass: string } => {
    if (rate === null || !indicatorDetail?.threshold) return { text: '—', badgeClass: 'badge-neutral' };
    const { alert1Value: a1, alert2Value: a2, alert3Value: a3 } = indicatorDetail.threshold;
    const dir = indicatorDetail.target?.targetDirection || 'BELOW';

    if (dir === 'BELOW') {
      if (rate >= a3) return { text: 'ALERT 3σ', badgeClass: 'badge-danger' };
      if (rate >= a2) return { text: 'ALERT 2σ', badgeClass: 'badge-warning' };
      if (rate >= a1) return { text: 'ALERT 1σ', badgeClass: 'badge-info' };
      return { text: 'NORMAL', badgeClass: 'badge-success' };
    } else {
      if (rate <= a3) return { text: 'ALERT 3σ', badgeClass: 'badge-danger' };
      if (rate <= a2) return { text: 'ALERT 2σ', badgeClass: 'badge-warning' };
      if (rate <= a1) return { text: 'ALERT 1σ', badgeClass: 'badge-info' };
      return { text: 'NORMAL', badgeClass: 'badge-success' };
    }
  };

  const displayValues = indicatorDetail?.history && indicatorDetail.history.length > 0
    ? indicatorDetail.history
    : (indicatorDetail?.recentValues || []).map((rv: any) => ({
        id: rv.id,
        indicatorId: rv.indicatorId,
        period: rv.period,
        numerator: rv.numeratorValue ?? rv.numerator ?? 0,
        denominator: rv.denominatorValue ?? rv.denominator ?? 0,
        calculatedValue: rv.computedValue ?? rv.calculatedValue ?? 0,
        alertLevel: rv.alertLevel ?? 'NORMAL',
        status: rv.status ?? 'LOCKED',
        notes: rv.notes,
        recordedBy: 'SYSTEM',
        recordedAt: rv.createdAt || '',
      }));

  const lastRate = indicatorDetail?.currentValue?.calculatedValue
    ?? (typeof indicatorDetail?.currentValue === 'number' ? indicatorDetail.currentValue : undefined)
    ?? (displayValues.length > 0 ? displayValues[displayValues.length - 1].calculatedValue : '-');

  const lastPeriod = indicatorDetail?.currentValue?.period
    ?? (displayValues.length > 0 ? displayValues[displayValues.length - 1].period : 'No Record');

  const targetDisplay = indicatorDetail?.target
    ? `${(indicatorDetail.target.targetDirection === 'BELOW' ? '≤ ' : '≥ ')}${indicatorDetail.target.targetValue}`
    : 'Unspecified';

  const alert3Display = indicatorDetail?.threshold?.alert3Value ?? '-';
  const alert1Display = indicatorDetail?.threshold?.alert1Value ?? '-';
  const alert2Display = indicatorDetail?.threshold?.alert2Value ?? '-';

  const validGridRows = gridRows.filter((r) => r.numerator !== '' && r.numerator !== null && r.numerator !== undefined);
  const gridCalculatedRates = validGridRows
    .map((r) => computeRowRate(r.numerator, r.denominator))
    .filter((r): r is number => r !== null && !isNaN(r));
  const annualGridMean = gridCalculatedRates.length > 0
    ? gridCalculatedRates.reduce((a, b) => a + b, 0) / gridCalculatedRates.length
    : null;

  const displayAvg = annualGridMean !== null
    ? annualGridMean.toFixed(2)
    : (indicatorDetail?.annualAverage !== null && indicatorDetail?.annualAverage !== undefined
        ? Number(indicatorDetail.annualAverage).toFixed(2)
        : '-');

  const baselineMeanDisplay = (indicatorDetail?.threshold?.baselineMean ?? (indicatorDetail?.threshold as any)?.meanValue) !== undefined
    ? Number(indicatorDetail?.threshold?.baselineMean ?? (indicatorDetail?.threshold as any)?.meanValue).toFixed(2)
    : '-';

  const multiYearData = (() => {
    const summaries = indicatorDetail?.annualSummaries || [];
    if (summaries.length > 0) return summaries;
    const fallbackYears = Array.from(new Set([new Date().getFullYear(), gridYear, 2025, 2024])).sort((a, b) => b - a);
    return fallbackYears.map((yr) => ({
      year: yr,
      recordedCount: yr === gridYear ? validGridRows.length : 0,
      averageValue: yr === gridYear ? annualGridMean : null,
      targetValue: yr === gridYear ? indicatorDetail?.target?.targetValue : undefined,
      targetDirection: yr === gridYear ? indicatorDetail?.target?.targetDirection : undefined,
      alert1Value: yr === gridYear ? indicatorDetail?.threshold?.alert1Value : undefined,
      alert2Value: yr === gridYear ? indicatorDetail?.threshold?.alert2Value : undefined,
      alert3Value: yr === gridYear ? indicatorDetail?.threshold?.alert3Value : undefined,
      baselineMean: yr === gridYear ? (indicatorDetail?.threshold?.baselineMean ?? (indicatorDetail?.threshold as any)?.meanValue) : undefined,
      latestValue: yr === gridYear && lastRate !== '-' ? Number(lastRate) : undefined,
      latestPeriod: yr === gridYear ? lastPeriod : undefined,
      latestAlertLevel: yr === gridYear ? indicatorDetail?.currentValue?.alertLevel : undefined,
    }));
  })();

  const dirtyRowsCount = gridRows.filter((r) => r.isDirty && r.numerator !== '').length;

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>
        {/* User-Friendly Header with proper margins and alignments */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span className="badge-pill badge-info" style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '12px' }}>
                {indicatorDetail?.code}
              </span>
              <span className="badge-pill badge-neutral" style={{ fontSize: '11px' }}>
                {indicatorDetail?.indicatorType}
              </span>
              <span className="badge-pill" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', border: '1px solid #e9d5ff', fontSize: '11px', fontWeight: 600 }}>
                {getNormalizedFrequency() === 'QUARTERLY' ? 'Quarterly Frequency' : getNormalizedFrequency() === 'ANNUAL' ? 'Annual Frequency' : 'Monthly Frequency'}
              </span>
              <span className={`badge-pill ${indicatorDetail?.status === 'ARCHIVED' ? 'badge-neutral' : 'badge-success'}`} style={{ fontSize: '11px' }}>
                {indicatorDetail?.status}
              </span>
              {/* Threshold Methodology Badge */}
              <span className="badge-pill" style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', fontSize: '11px', fontWeight: 600 }}>
                {indicatorDetail?.thresholdMethodology === 'SPECIFIC_TARGET' ? '🎯 Specific Target' : '📐 EASA 3-Sigma'}
              </span>
              {/* SPI Owner Department Badge */}
              <span style={{ fontSize: '11px', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                Owner: {ownerDept}
              </span>

              {/* Data Provider Department Badge */}
              <span style={{ fontSize: '11px', color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                Data Provider: {providerDept}
              </span>

              {/* Stakeholder Departments Badge */}
              {indicatorDetail?.stakeholderDepartments && indicatorDetail.stakeholderDepartments.length > 0 && (
                <span style={{ fontSize: '11px', color: '#6d28d9', backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                  Stakeholders: {indicatorDetail.stakeholderDepartments.join(', ')}
                </span>
              )}

              {/* Active User Responsibility Status */}
              {isSmsUser ? (
                <span className="badge-pill" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: '11px', fontWeight: 600 }}>
                  SMS Authorized (Company-Wide Access)
                </span>
              ) : isStakeholder ? (
                <span className="badge-pill" style={{ backgroundColor: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', fontSize: '11px', fontWeight: 700 }}>
                  👀 Stakeholder Observer (Read-Only)
                </span>
              ) : isDataProvider && isOwner ? (
                <span className="badge-pill" style={{ backgroundColor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '11px', fontWeight: 700 }}>
                  Owner & Data Provider (Full Data Access)
                </span>
              ) : isDataProvider ? (
                <span className="badge-pill" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', fontSize: '11px', fontWeight: 700 }}>
                  Data Provider ({currentUserDept} - Data Entry Authorized)
                </span>
              ) : isOwner ? (
                <span className="badge-pill" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', fontSize: '11px', fontWeight: 700 }}>
                  SPI Owner ({currentUserDept} - Target Custodian)
                </span>
              ) : (
                <span className="badge-pill" style={{ backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', fontSize: '11px' }}>
                  Read-Only ({currentUserDept || 'Other Dept'})
                </span>
              )}
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', margin: '0 0 6px 0' }}>
              {indicatorDetail?.name}
            </h3>
            {indicatorDetail?.description && (
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                {indicatorDetail.description}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isSmsUser && (
              <button
                type="button"
                disabled={archiving}
                onClick={handleToggleArchive}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: indicatorDetail?.status === 'ARCHIVED' ? '1px solid #16a34a' : '1px solid #dc2626',
                  background: indicatorDetail?.status === 'ARCHIVED' ? '#f0fdf4' : '#fef2f2',
                  color: indicatorDetail?.status === 'ARCHIVED' ? '#166534' : '#991b1b',
                  cursor: archiving ? 'not-allowed' : 'pointer'
                }}
              >
                {archiving ? 'Updating...' : indicatorDetail?.status === 'ARCHIVED' ? '♻️ Unarchive SPI' : '📦 Archive SPI'}
              </button>
            )}
            <button
              onClick={onClose}
              className="modal-close-btn"
              title="Close"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tab Navigation with clean spacing */}
        <div className="nav-tabs" style={{ padding: '0 28px', margin: '0', borderBottom: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex' }}>
            <button
              onClick={() => setActiveTab('GRID')}
              className={`tab-btn ${activeTab === 'GRID' ? 'active' : ''}`}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Table size={16} /> Excel Grid Fast Data Entry
              {dirtyRowsCount > 0 && (
                <span style={{ backgroundColor: '#ef4444', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '10px' }}>
                  {dirtyRowsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('VALUES')}
              className={`tab-btn ${activeTab === 'VALUES' ? 'active' : ''}`}
            >
              Historical Measurements & Trend
            </button>
            <button
              onClick={() => setActiveTab('CONFIG')}
              className={`tab-btn ${activeTab === 'CONFIG' ? 'active' : ''}`}
            >
              Target & 3-Sigma Threshold Settings
            </button>
            <button
              onClick={() => {
                setActiveTab('ACTIONS_RISKS');
                loadActionsAndRisks();
              }}
              className={`tab-btn ${activeTab === 'ACTIONS_RISKS' ? 'active' : ''}`}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Shield size={16} /> Linked Actions & Risks
              {(linkedActions.length > 0 || linkedRisks.length > 0) && (
                <span style={{ backgroundColor: '#0284c7', color: '#fff', fontSize: '10px', padding: '1px 6px', borderRadius: '10px', fontWeight: 700 }}>
                  {linkedActions.length + linkedRisks.length}
                </span>
              )}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Year Switcher */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Year:</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                <button
                  type="button"
                  onClick={() => setGridYear((prev) => prev - 1)}
                  title="Previous Year"
                  style={{ padding: '4px 8px', fontSize: '11px', border: 'none', background: '#f8fafc', cursor: 'pointer', borderRight: '1px solid #cbd5e1', color: '#475569' }}
                >
                  ◄
                </button>
                <select
                  value={gridYear}
                  onChange={(e) => setGridYear(Number(e.target.value))}
                  style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 700, border: 'none', background: '#ffffff', color: '#0f172a', cursor: 'pointer', outline: 'none' }}
                >
                  {[2027, 2026, 2025, 2024, 2023, 2022].map((y) => (
                    <option key={y} value={y}>
                      {y} {y === new Date().getFullYear() ? '(Current Year)' : '(Historical)'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setGridYear((prev) => prev + 1)}
                  title="Next Year"
                  style={{ padding: '4px 8px', fontSize: '11px', border: 'none', background: '#f8fafc', cursor: 'pointer', borderLeft: '1px solid #cbd5e1', color: '#475569' }}
                >
                  ►
                </button>
              </div>
            </div>

            {activeTab === 'GRID' && canEditIndicator && (
              <button
                type="button"
                onClick={handleSaveAllGrid}
                disabled={savingBatch || dirtyRowsCount === 0}
                className="btn-primary"
                style={{
                  fontSize: '12px',
                  padding: '6px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: dirtyRowsCount > 0 ? '#10b981' : undefined,
                  borderColor: dirtyRowsCount > 0 ? '#059669' : undefined,
                }}
              >
                <Save size={14} />
                {savingBatch ? 'Saving...' : `Save All Changes (${dirtyRowsCount})`}
              </button>
            )}
          </div>
        </div>

        {/* Content Body with generous breathing room */}
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', background: '#f8fafc', padding: '24px 28px' }}>
          {/* Universal Annual KPI Summary Bar for Selected Year (gridYear) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Son Ölçüm / Latest
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginTop: '3px' }}>
                {lastRate}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', fontFamily: 'monospace' }}>
                Dönem: {lastPeriod}
              </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Yıl Ortalaması ({gridYear})
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#0284c7', marginTop: '3px' }}>
                {displayAvg}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                {validGridRows.length} dönem | μ: {baselineMeanDisplay}
              </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Hedef / Target ({gridYear})
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#1d4ed8', marginTop: '3px' }}>
                {targetDisplay}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                Birim: {indicatorDetail?.unit || 'PER_1000_FLIGHTS'}
              </div>
            </div>

            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Alert 1 (1σ Caution)
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#b45309', marginTop: '3px' }}>
                {alert1Display}
              </div>
              <div style={{ fontSize: '11px', color: '#b45309', marginTop: '2px' }}>
                1-Sigma Dikkat Eşiği
              </div>
            </div>

            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Alert 2 (2σ Warning)
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#ea580c', marginTop: '3px' }}>
                {alert2Display}
              </div>
              <div style={{ fontSize: '11px', color: '#ea580c', marginTop: '2px' }}>
                2-Sigma Uyarı Eşiği
              </div>
            </div>

            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Alert 3 (3σ Critical)
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#dc2626', marginTop: '3px' }}>
                {alert3Display}
              </div>
              <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                3-Sigma Kritik Alarm
              </div>
            </div>
          </div>
          {/* TAB 1: EXCEL GRID EDITING */}
          {activeTab === 'GRID' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Information Banner */}
              <div style={{ backgroundColor: canEditIndicator ? '#eff6ff' : '#f8fafc', border: `1px solid ${canEditIndicator ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Info size={18} color={canEditIndicator ? '#2563eb' : '#64748b'} />
                  <span style={{ fontSize: '13px', color: canEditIndicator ? '#1e40af' : '#475569' }}>
                    {canEditIndicator
                      ? isSmsUser
                        ? `SMS Personeli: ${gridYear} yılı Ocak-Aralık (12 Ay) veri giriş alanları hazır durumdadır. Pay/payda değerlerini girip toplu kaydedebilirsiniz.`
                        : isDataProvider
                        ? `Veri Sağlayıcı (${providerDept}): ${gridYear} yılı Ocak-Aralık veri giriş alanları hazır durumdadır. Ölçümleri girip kaydedebilirsiniz.`
                        : `SPI Sahibi (${ownerDept}): ${gridYear} yılı 12 aylık veri tablosu hazır durumdadır.`
                      : `Salt Okunur Mod: Bu gösterge ${ownerDept} birimine aittir, veri sağlayıcı ${providerDept}.`}
                  </span>
                </div>
                
              </div>

              {/* Editable Excel Grid Table */}
              <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                    {getNormalizedFrequency() === 'QUARTERLY'
                      ? 'Quarterly Fast Entry Grid (Q1 - Q4)'
                      : getNormalizedFrequency() === 'ANNUAL'
                      ? 'Annual Fast Entry Grid'
                      : 'Monthly Fast Entry Grid (Ocak - Aralık / 12 Ay)'} - Year {gridYear} ({gridRows.length} Periods, Frequency: {getNormalizedFrequency()}, Unit: {indicatorDetail?.unit || 'PER_1000_FLIGHTS'})
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Cadence: {getNormalizedFrequency()}. Values entered will be saved for year {gridYear}. Modified rows are highlighted.
                  </div>
                </div>

                <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0, maxHeight: '55vh', overflowY: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, backgroundColor: '#f1f5f9', zIndex: 2 }}>
                        <th style={{ minWidth: '150px' }}>Period ({getNormalizedFrequency() === 'QUARTERLY' ? 'Quarter' : getNormalizedFrequency() === 'ANNUAL' ? 'Year' : 'Month'})</th>
                        <th style={{ width: '140px' }}>Numerator (Event Count)</th>
                        <th style={{ width: '150px' }}>Denominator (Flights/Hours)</th>
                        <th style={{ width: '130px', textAlign: 'right' }}>Calculated Rate</th>
                        <th style={{ width: '120px', textAlign: 'center' }}>Threshold Status</th>
                        <th style={{ minWidth: '220px' }}>Audit Notes / Justification</th>
                        <th style={{ width: '80px', textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gridRows.map((row, idx) => {
                        const rate = computeRowRate(row.numerator, row.denominator);
                        const alertInfo = evaluateAlert(rate);
                        const displayInfo = getPeriodDisplayInfo(row.period, getNormalizedFrequency());
                        return (
                          <tr
                            key={row.period}
                            style={{
                              backgroundColor: row.isDirty ? '#fefce8' : undefined,
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px' }}>
                                {displayInfo.title}
                                {row.isDirty && (
                                  <span style={{ marginLeft: '6px', color: '#d97706', fontSize: '11px', fontWeight: 700 }}>*</span>
                                )}
                              </div>
                              <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b' }}>
                                {row.period}
                              </div>
                            </td>
                            <td>
                              <input
                                type="number"
                                step="any"
                                value={row.numerator}
                                onChange={(e) => handleGridCellChange(idx, 'numerator', e.target.value)}
                                disabled={!canEditIndicator}
                                placeholder="0"
                                style={{
                                  width: '100%',
                                  padding: '6px 10px',
                                  fontSize: '13px',
                                  fontFamily: 'monospace',
                                  border: row.isDirty ? '1px solid #facc15' : '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  backgroundColor: !canEditIndicator ? '#f1f5f9' : '#fff',
                                  color: !canEditIndicator ? '#475569' : '#0f172a',
                                  cursor: !canEditIndicator ? 'not-allowed' : 'auto',
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="any"
                                value={row.denominator}
                                onChange={(e) => handleGridCellChange(idx, 'denominator', e.target.value)}
                                disabled={!canEditIndicator}
                                placeholder="1000"
                                style={{
                                  width: '100%',
                                  padding: '6px 10px',
                                  fontSize: '13px',
                                  fontFamily: 'monospace',
                                  border: row.isDirty ? '1px solid #facc15' : '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  backgroundColor: !canEditIndicator ? '#f1f5f9' : '#fff',
                                  color: !canEditIndicator ? '#475569' : '#0f172a',
                                  cursor: !canEditIndicator ? 'not-allowed' : 'auto',
                                }}
                              />
                            </td>
                            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                              {rate !== null ? rate : '—'}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`badge-pill ${alertInfo.badgeClass}`}>
                                {alertInfo.text}
                              </span>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={row.notes}
                                onChange={(e) => handleGridCellChange(idx, 'notes', e.target.value)}
                                disabled={!canEditIndicator}
                                placeholder={!canEditIndicator ? '' : 'Add verification note...'}
                                style={{
                                  width: '100%',
                                  padding: '6px 10px',
                                  fontSize: '12px',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '6px',
                                  backgroundColor: !canEditIndicator ? '#f1f5f9' : '#fff',
                                  color: !canEditIndicator ? '#475569' : '#0f172a',
                                  cursor: !canEditIndicator ? 'not-allowed' : 'auto',
                                }}
                              />
                            </td>
                            <td style={{ textAlign: 'center', fontSize: '11px', color: '#64748b' }}>
                              {row.isDirty ? (
                                <span style={{ color: '#d97706', fontWeight: 600 }}>MODIFIED</span>
                              ) : (
                                <span style={{ color: '#10b981' }}>SAVED</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ padding: '12px 18px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    * Highlighted yellow rows represent unsaved pending changes.
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveAllGrid}
                    disabled={savingBatch || dirtyRowsCount === 0}
                    className="btn-primary"
                    style={{
                      fontSize: '13px',
                      padding: '8px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: dirtyRowsCount > 0 ? '#10b981' : undefined,
                      borderColor: dirtyRowsCount > 0 ? '#059669' : undefined,
                    }}
                  >
                    <Save size={15} />
                    {savingBatch ? 'Saving...' : `Save All Changes (${dirtyRowsCount})`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: HISTORICAL LOG & TREND */}
          {activeTab === 'VALUES' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Annual Multi-Year Performance Overview Table */}
              <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #cbd5e1', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📅 Annual Performance History (Yıllar İtibarıyla Performans, Ortalama ve Limitler)</span>
                    </h4>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      Geçmiş ve mevcut tüm yılların gerçekleşen ortalama değerleri, hedefleri (SPT) ve 1σ / 2σ / 3σ limit karşılaştırması.
                    </p>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#0284c7', backgroundColor: '#e0f2fe', padding: '3px 8px', borderRadius: '4px' }}>
                    Multi-Year Trend
                  </span>
                </div>

                <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Performance Year</th>
                        <th>Recorded Periods</th>
                        <th>Annual Mean (Ortalama)</th>
                        <th>Target (Hedef / SPT)</th>
                        <th>Alert 1 (1σ)</th>
                        <th>Alert 2 (2σ)</th>
                        <th>Alert 3 (3σ)</th>
                        <th style={{ textAlign: 'center' }}>Year Status</th>
                        <th style={{ textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {multiYearData.map((row) => (
                        <tr key={row.year} style={{ backgroundColor: row.year === gridYear ? '#f0f9ff' : undefined }}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a' }}>{row.year}</span>
                              {row.year === new Date().getFullYear() ? (
                                <span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: '4px' }}>
                                  Current Year
                                </span>
                              ) : row.year < new Date().getFullYear() ? (
                                <span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                                  Historical
                                </span>
                              ) : (
                                <span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: '4px' }}>
                                  Future
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                              {row.recordedCount > 0 ? `${row.recordedCount} Dönem` : 'Kayıt Yok'}
                            </span>
                          </td>
                          <td>
                            {row.averageValue !== null && row.averageValue !== undefined ? (
                              <div>
                                <span style={{ fontWeight: 700, color: '#0369a1', fontSize: '14px' }}>
                                  {Number(row.averageValue).toFixed(2)}
                                </span>
                                {row.baselineMean !== undefined && (
                                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    μ: {Number(row.baselineMean).toFixed(2)}
                                  </div>
                                )}
                              </div>
                            ) : row.baselineMean !== undefined ? (
                              <div>
                                <span style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>
                                  μ: {Number(row.baselineMean).toFixed(2)}
                                </span>
                                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Baseline</div>
                              </div>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                            )}
                          </td>
                          <td>
                            {row.targetValue !== undefined && row.targetValue !== null ? (
                              <span style={{ fontWeight: 700, color: '#1d4ed8', fontSize: '13px' }}>
                                {row.targetDirection === 'BELOW' ? '≤ ' : '≥ '}
                                {row.targetValue}
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '12px' }}>Belirtilmedi</span>
                            )}
                          </td>
                          <td>
                            <span style={{ color: '#b45309', fontWeight: 700, fontFamily: 'monospace', fontSize: '12px' }}>
                              {row.alert1Value ?? '—'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#ea580c', fontWeight: 700, fontFamily: 'monospace', fontSize: '12px' }}>
                              {row.alert2Value ?? '—'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#dc2626', fontWeight: 800, fontFamily: 'monospace', fontSize: '12px' }}>
                              {row.alert3Value ?? '—'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {getAlertBadge(row.latestAlertLevel)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {row.year === gridYear ? (
                              <span style={{ fontSize: '11px', color: '#0284c7', fontWeight: 700, backgroundColor: '#e0f2fe', padding: '4px 8px', borderRadius: '4px' }}>
                                ✓ Açık Yıl
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setGridYear(row.year)}
                                className="btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 10px', whiteSpace: 'nowrap' }}
                              >
                                {row.year} Verilerini Aç →
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* History Table */}
              <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                    Historical Measurement Records - Year {gridYear} ({displayValues.length} Entries)
                  </h4>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Audit Verified</span>
                </div>
                <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Numerator (Events)</th>
                        <th>Denominator (Flights)</th>
                        <th>Calculated Rate</th>
                        <th style={{ textAlign: 'center' }}>Threshold Level</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                        <th>Auditor Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayValues.length === 0 ? (
                        <tr>
                          <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                            No recorded data found for year {gridYear}. You can enter new data via the "Excel Grid Fast Data Entry" tab.
                          </td>
                        </tr>
                      ) : (
                        displayValues.map((val) => {
                          const periodInfo = getPeriodDisplayInfo(val.period, getNormalizedFrequency(indicatorDetail?.frequency));
                          return (
                            <tr key={val.id || val.period}>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px' }}>{periodInfo.title}</div>
                                <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b' }}>{val.period}</div>
                              </td>
                              <td style={{ fontFamily: 'monospace', color: '#475569' }}>{val.numerator}</td>
                              <td style={{ fontFamily: 'monospace', color: '#475569' }}>{val.denominator}</td>
                              <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                                {val.calculatedValue}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  className={`badge-pill ${
                                    val.alertLevel === 'NORMAL'
                                      ? 'badge-success'
                                      : val.alertLevel === 'ALERT_1_SIGMA'
                                      ? 'badge-info'
                                      : val.alertLevel === 'ALERT_2_SIGMA'
                                      ? 'badge-warning'
                                      : 'badge-danger'
                                  }`}
                                >
                                  {val.alertLevel}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center', fontSize: '12px', color: '#64748b' }}>{val.status}</td>
                              <td style={{ fontSize: '12px', color: '#64748b' }}>{val.notes || '—'}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: TARGET & 3-SIGMA THRESHOLDS */}
          {activeTab === 'CONFIG' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* SPI Ownership & Data Provider Governance Card */}
              <div style={{ backgroundColor: '#ffffff', padding: '18px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <Users size={17} color="#0284c7" /> SPI Governance: Ownership & Data Provider Assignments
                    </h4>
                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b' }}>
                      One department owns the SPI objective while another department can be assigned as the operational Data Provider.
                    </p>
                  </div>
                  {isSmsUser && (
                    <button
                      type="button"
                      onClick={handleSaveRoles}
                      disabled={savingRoles}
                      className="btn-secondary"
                      style={{ fontSize: '12px', padding: '6px 14px', width: 'auto', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <Save size={13} /> {savingRoles ? 'Saving Governance...' : 'Update Governance & Frequency'}
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#0369a1', marginBottom: '4px' }}>
                      ● SPI Owner Department (Sahiplik)
                    </label>
                    {isSmsUser ? (
                      <select
                        value={editOwnerDept}
                        onChange={(e) => setEditOwnerDept(e.target.value)}
                        style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                      >
                        {deptList.map((d) => (
                          <option key={d.id} value={d.name}>
                            {d.name} ({d.department_code})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ padding: '7px 12px', backgroundColor: '#f0f9ff', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#0369a1', border: '1px solid #bae6fd' }}>
                        {ownerDept}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#b45309', marginBottom: '4px' }}>
                      ● Data Provider Department (Veri Giriş Sorumlusu)
                    </label>
                    {isSmsUser ? (
                      <select
                        value={editProviderDept}
                        onChange={(e) => setEditProviderDept(e.target.value)}
                        style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                      >
                        {deptList.map((d) => (
                          <option key={d.id} value={d.name}>
                            {d.name} ({d.department_code})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ padding: '7px 12px', backgroundColor: '#fffbeb', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#b45309', border: '1px solid #fde68a' }}>
                        {providerDept}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#4338ca', marginBottom: '4px' }}>
                      ● Measurement Frequency (Cadence)
                    </label>
                    {isSmsUser ? (
                      <select
                        value={editFrequency}
                        onChange={(e) => setEditFrequency(e.target.value as any)}
                        style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                      >
                        <option value="MONTHLY">Monthly (12 Periods: YYYY-01 .. 12)</option>
                        <option value="QUARTERLY">Quarterly (4 Periods: YYYY-Q1 .. Q4)</option>
                        <option value="ANNUALLY">Annual / Annually (1 Period: YYYY)</option>
                      </select>
                    ) : (
                      <div style={{ padding: '7px 12px', backgroundColor: '#eef2ff', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#4338ca', border: '1px solid #c7d2fe' }}>
                        {getNormalizedFrequency(editFrequency)}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                    Data Provider Instructions / Source System
                  </label>
                  {isSmsUser ? (
                    <input
                      type="text"
                      value={editProviderInstructions}
                      onChange={(e) => setEditProviderInstructions(e.target.value)}
                      placeholder="e.g. Monthly OFDM flight exceedances summary or TechLog defects"
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                    />
                  ) : (
                    <div style={{ padding: '7px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '12px', color: '#475569', border: '1px solid #e2e8f0' }}>
                      {indicatorDetail?.dataProviderInstructions || 'Standard monthly data feed.'}
                    </div>
                  )}
                </div>

                {/* Stakeholder Departments */}
                <div style={{ paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', fontWeight: 700, color: '#6d28d9', marginBottom: '6px' }}>
                    <span>Stakeholder Departments (Read-Only Observers)</span>
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>
                      Can observe indicators and receive breach alerts without edit rights
                    </span>
                  </label>
                  {isSmsUser ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {deptList.map((d) => {
                        const isSelected = editStakeholders.includes(d.name);
                        const isOwnerOrProvider = d.name === editOwnerDept || d.name === editProviderDept;
                        if (isOwnerOrProvider) return null;
                        return (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => {
                              setEditStakeholders((prev) =>
                                isSelected ? prev.filter((name) => name !== d.name) : [...prev, d.name]
                              );
                            }}
                            style={{
                              padding: '4px 10px',
                              fontSize: '11px',
                              borderRadius: '16px',
                              border: isSelected ? '1px solid #7c3aed' : '1px solid #cbd5e1',
                              background: isSelected ? '#f5f3ff' : '#ffffff',
                              color: isSelected ? '#6d28d9' : '#475569',
                              fontWeight: isSelected ? 700 : 500,
                              cursor: 'pointer'
                            }}
                          >
                            <span>{isSelected ? '✓ ' : '+ '}</span>
                            <span>{d.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      {indicatorDetail?.stakeholderDepartments && indicatorDetail.stakeholderDepartments.length > 0
                        ? indicatorDetail.stakeholderDepartments.join(', ')
                        : 'No stakeholder departments assigned.'}
                    </div>
                  )}
                </div>

                {/* Threshold Methodology Card in Config */}
                <div style={{ paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                    Alert Level & Threshold Methodology (Item 13)
                  </label>
                  {isSmsUser ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div
                          onClick={() => setEditMethodology('THREE_SIGMA')}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: editMethodology === 'THREE_SIGMA' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                            background: editMethodology === 'THREE_SIGMA' ? '#eff6ff' : '#ffffff',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: '12px', color: editMethodology === 'THREE_SIGMA' ? '#1e40af' : '#334155' }}>
                            📐 EASA 3-Sigma Methodology
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            Calculated dynamically via baseline mean and std dev limits.
                          </div>
                        </div>

                        <div
                          onClick={() => setEditMethodology('SPECIFIC_TARGET')}
                          style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: editMethodology === 'SPECIFIC_TARGET' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                            background: editMethodology === 'SPECIFIC_TARGET' ? '#eff6ff' : '#ffffff',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: '12px', color: editMethodology === 'SPECIFIC_TARGET' ? '#1e40af' : '#334155' }}>
                            🎯 Specific Target Limits
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            Fixed numerical Amber Warning and Red Critical limits.
                          </div>
                        </div>
                      </div>

                      {editMethodology === 'SPECIFIC_TARGET' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#d97706', marginBottom: '3px' }}>
                              Warning Amber Limit Value
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={editAmberVal}
                              onChange={(e) => setEditAmberVal(e.target.value === '' ? '' : Number(e.target.value))}
                              placeholder="e.g. 2.50"
                              style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#dc2626', marginBottom: '3px' }}>
                              Critical Red Limit Value
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={editRedVal}
                              onChange={(e) => setEditRedVal(e.target.value === '' ? '' : Number(e.target.value))}
                              placeholder="e.g. 4.00"
                              style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      Methodology: <strong>{indicatorDetail?.thresholdMethodology === 'SPECIFIC_TARGET' ? 'Specific Target Limits' : 'EASA 3-Sigma Methodology'}</strong>
                      {indicatorDetail?.thresholdMethodology === 'SPECIFIC_TARGET' && (
                        <span> (Amber: {indicatorDetail.targetAmberValue ?? '—'}, Red: {indicatorDetail.targetRedValue ?? '—'})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleSaveConfig} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Target Settings */}
              <div style={{ backgroundColor: '#ffffff', padding: '18px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Safety Target (SPT)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Target Year</label>
                    <input
                      type="number"
                      value={targetYear}
                      onChange={(e) => setTargetYear(Number(e.target.value))}
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Target Direction</label>
                    <select
                      value={targetDirection}
                      onChange={(e) => setTargetDirection(e.target.value as TargetDirection)}
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                    >
                      <option value="BELOW">Keep Below Target (≤ Target - Lagging)</option>
                      <option value="ABOVE">Keep Above Target (≥ Target - Leading)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Target Value</label>
                    <input
                      type="number"
                      step="any"
                      value={targetValue}
                      onChange={(e) => setTargetValue(Number(e.target.value))}
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace' }}
                    />
                  </div>
                </div>
              </div>

              {/* Threshold 1σ, 2σ, 3σ Settings */}
              <div style={{ backgroundColor: '#fffbeb', padding: '18px 20px', borderRadius: '10px', border: '1px solid #fef3c7', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#92400e' }}>
                    Statistical Threshold Limits (1σ, 2σ, 3σ Safety Alert Levels)
                  </h4>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#b45309' }}>
                    Tolerance bands based on mean (μ) and standard deviation (σ) per ICAO Annex 19 & Doc 9859 standards.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Baseline Reference Period</label>
                    <input
                      type="text"
                      value={baselinePeriod}
                      onChange={(e) => setBaselinePeriod(e.target.value)}
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Baseline Mean (μ)</label>
                    <input
                      type="number"
                      step="any"
                      value={baselineMean}
                      onChange={(e) => setBaselineMean(Number(e.target.value))}
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', backgroundColor: '#fff' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Standard Deviation (σ)</label>
                    <input
                      type="number"
                      step="any"
                      value={baselineStdDev}
                      onChange={(e) => setBaselineStdDev(Number(e.target.value))}
                      style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', backgroundColor: '#fff' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
                  <div style={{ padding: '12px', backgroundColor: '#fef9c3', borderRadius: '8px', border: '1px solid #fde047' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#854d0e', marginBottom: '4px' }}>
                      1σ Alert Threshold (Yellow / Caution)
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={alert1Value}
                      onChange={(e) => setAlert1Value(Number(e.target.value))}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid #fde047', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', backgroundColor: '#fff' }}
                    />
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#ffedd5', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#9a3412', marginBottom: '4px' }}>
                      2σ Alert Threshold (Orange / Warning)
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={alert2Value}
                      onChange={(e) => setAlert2Value(Number(e.target.value))}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid #fed7aa', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', backgroundColor: '#fff' }}
                    />
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#fee2e2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#991b1b', marginBottom: '4px' }}>
                      3σ Critical Alarm Threshold (Red / SRB Action)
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={alert3Value}
                      onChange={(e) => setAlert3Value(Number(e.target.value))}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', backgroundColor: '#fff' }}
                    />
                  </div>
                </div>
              </div>

              {!isSmsUser && (
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <AlertTriangle size={18} color="#dc2626" />
                  <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: 600 }}>
                    Target definition and 3-Sigma threshold calculations are restricted to SMS personnel only.
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '6px' }}>
                {isSmsUser ? (
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                    style={{ fontSize: '13px', padding: '9px 24px' }}
                  >
                    {loading ? 'Saving...' : 'Save Targets & Thresholds'}
                  </button>
                ) : (
                  <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                    * Configuration modifications require SMS personnel permissions.
                  </div>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Tab 4: SPI-Linked Actions and Risks (Item 4) */}
        {activeTab === 'ACTIONS_RISKS' && (
          <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Header Banner */}
            <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', maxWidth: '750px' }}>
                <Shield size={24} color="#0284c7" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#0369a1' }}>
                    SPI Governance: Corrective Actions (CAPA) & Operational Risk Assessments
                  </div>
                  <div style={{ fontSize: '13px', color: '#0284c7', marginTop: '3px', lineHeight: 1.5 }}>
                    Under ICAO Annex 19 & SMS safety management, when safety performance indicators exhibit adverse trends or breach 1σ/2σ/3σ threshold triggers, the <strong>SPI Owner ({ownerDept})</strong> and SMS personnel can define targeted corrective actions or formal risk register assessments.
                  </div>
                  <div style={{ marginTop: '6px' }}>
                    {canDefineActionRisk ? (
                      <span className="badge-pill" style={{ backgroundColor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '11px', fontWeight: 700 }}>
                        ✓ You have authorization to define Safety Actions and Risks for this indicator
                      </span>
                    ) : (
                      <span className="badge-pill" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', fontSize: '11px' }}>
                        ℹ Read-Only View — Action & Risk creation is authorized for SPI Owner ({ownerDept}) and SMS personnel
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {canDefineActionRisk && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setNewActionDepartment(ownerDept);
                      setShowAddActionModal(true);
                    }}
                    className="btn-primary"
                    style={{ fontSize: '12px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#0284c7', borderColor: '#0369a1' }}
                  >
                    <Plus size={15} /> Define Safety Action (CAPA)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewRiskDepartment(ownerDept);
                      setShowAddRiskModal(true);
                    }}
                    className="btn-primary"
                    style={{ fontSize: '12px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#dc2626', borderColor: '#b91c1c' }}
                  >
                    <Plus size={15} /> Identify Safety Risk
                  </button>
                </div>
              )}
            </div>

            {/* Section 1: Linked Safety Actions (CAPA) */}
            <div className="card" style={{ padding: '20px', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    Linked Safety Actions (CAPA)
                  </h4>
                  <span className="badge-pill badge-neutral" style={{ fontSize: '11px', fontWeight: 700 }}>
                    {linkedActions.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => loadActionsAndRisks()}
                  disabled={loadingActionsRisks}
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <RefreshCw size={12} className={loadingActionsRisks ? 'animate-spin' : ''} /> Refresh
                </button>
              </div>

              {loadingActionsRisks ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading linked actions...</div>
              ) : linkedActions.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>No Safety Actions linked to this indicator.</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                    When threshold alarms trigger (1σ, 2σ, 3σ), define an action to track mitigation and prevent recurrences.
                  </div>
                </div>
              ) : (
                <div className="table-container" style={{ margin: 0 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Action #</th>
                        <th>Title / Mitigation Scope</th>
                        <th>Type</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Department / Assignee</th>
                        <th>Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedActions.map((act) => (
                        <tr key={act.id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0284c7', fontSize: '12px' }}>
                            {act.actionNumber}
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}>{act.title}</div>
                            {act.description && (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{act.description}</div>
                            )}
                          </td>
                          <td>
                            <span className="badge-pill" style={{
                              fontSize: '11px',
                              backgroundColor: act.actionType === 'CORRECTIVE' ? '#fef3c7' : act.actionType === 'PREVENTIVE' ? '#e0f2fe' : '#f1f5f9',
                              color: act.actionType === 'CORRECTIVE' ? '#92400e' : act.actionType === 'PREVENTIVE' ? '#0369a1' : '#475569',
                              fontWeight: 600,
                            }}>
                              {act.actionType}
                            </span>
                          </td>
                          <td>
                            <span className="badge-pill" style={{
                              fontSize: '11px',
                              backgroundColor: act.priority === 'IMMEDIATE' || act.priority === 'HIGH' ? '#fee2e2' : act.priority === 'MEDIUM' ? '#fef3c7' : '#f1f5f9',
                              color: act.priority === 'IMMEDIATE' || act.priority === 'HIGH' ? '#991b1b' : act.priority === 'MEDIUM' ? '#92400e' : '#475569',
                              fontWeight: 600,
                            }}>
                              {act.priority}
                            </span>
                          </td>
                          <td>
                            <span className="badge-pill badge-neutral" style={{ fontSize: '11px' }}>
                              {act.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: '#475569' }}>
                            <div>{act.department || 'General'}</div>
                            {act.assigneeName && (
                              <div style={{ fontSize: '11px', color: '#64748b' }}>{act.assigneeName}</div>
                            )}
                          </td>
                          <td style={{ fontSize: '12px', fontFamily: 'monospace', color: '#334155' }}>
                            {act.currentDueDate ? act.currentDueDate.split('T')[0] : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 2: Linked Identified Safety Risks */}
            <div className="card" style={{ padding: '20px', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    Linked Identified Safety Risks
                  </h4>
                  <span className="badge-pill badge-neutral" style={{ fontSize: '11px', fontWeight: 700 }}>
                    {linkedRisks.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => loadActionsAndRisks()}
                  disabled={loadingActionsRisks}
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <RefreshCw size={12} className={loadingActionsRisks ? 'animate-spin' : ''} /> Refresh
                </button>
              </div>

              {loadingActionsRisks ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading linked risks...</div>
              ) : linkedRisks.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>No Safety Risks linked to this indicator.</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                    Identify operational hazards or precursors monitored by this SPI to record formal 5x5 risk assessments.
                  </div>
                </div>
              ) : (
                <div className="table-container" style={{ margin: 0 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Risk #</th>
                        <th>Risk Title / Precursor</th>
                        <th>Matrix Index</th>
                        <th>Tolerability</th>
                        <th>Status</th>
                        <th>Department</th>
                        <th>Date Recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedRisks.map((rsk) => (
                        <tr key={rsk.id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#dc2626', fontSize: '12px' }}>
                            {rsk.riskNumber}
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}>{rsk.title}</div>
                            {rsk.description && (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{rsk.description}</div>
                            )}
                          </td>
                          <td>
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                              {rsk.initialRiskIndex || '—'}
                            </span>
                          </td>
                          <td>
                            <span className="badge-pill" style={{
                              fontSize: '11px',
                              backgroundColor: rsk.initialTolerability === 'INTOLERABLE' ? '#fee2e2' : rsk.initialTolerability === 'TOLERABLE' ? '#fef3c7' : '#ecfdf5',
                              color: rsk.initialTolerability === 'INTOLERABLE' ? '#991b1b' : rsk.initialTolerability === 'TOLERABLE' ? '#92400e' : '#047857',
                              fontWeight: 700,
                            }}>
                              {rsk.initialTolerability || 'TOLERABLE'}
                            </span>
                          </td>
                          <td>
                            <span className="badge-pill badge-neutral" style={{ fontSize: '11px' }}>
                              {rsk.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: '#475569' }}>
                            {rsk.department || 'General'}
                          </td>
                          <td style={{ fontSize: '12px', fontFamily: 'monospace', color: '#64748b' }}>
                            {rsk.createdAt ? rsk.createdAt.split('T')[0] : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Modal: Define Safety Action (CAPA) */}
      {showAddActionModal && (
        <div className="modal-backdrop" style={{ zIndex: 10001 }}>
          <div className="modal-card" style={{ maxWidth: '600px', width: '100%', backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={18} color="#0284c7" />
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#0f172a' }}>
                  Define Safety Action (CAPA) for {indicatorDetail?.code}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddActionModal(false)}
                className="modal-close-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateAction} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                  Action Title / Primary Objective *
                </label>
                <input
                  type="text"
                  required
                  value={newActionTitle}
                  onChange={(e) => setNewActionTitle(e.target.value)}
                  placeholder="e.g. Conduct recurrent unstabilized approach simulator training"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Action Type</label>
                  <select
                    value={newActionType}
                    onChange={(e) => setNewActionType(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  >
                    <option value="CORRECTIVE">Corrective (Eliminates root causes)</option>
                    <option value="PREVENTIVE">Preventive (Mitigates recurrence risk)</option>
                    <option value="CONTAINMENT">Containment (Immediate barrier)</option>
                  </select>
                </div>

                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Priority Level</label>
                  <select
                    value={newActionPriority}
                    onChange={(e) => setNewActionPriority(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  >
                    <option value="IMMEDIATE">IMMEDIATE (Safety Critical)</option>
                    <option value="HIGH">HIGH Priority</option>
                    <option value="MEDIUM">MEDIUM Priority</option>
                    <option value="LOW">LOW Priority</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Responsible Department</label>
                  <select
                    value={newActionDepartment || ownerDept}
                    onChange={(e) => setNewActionDepartment(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  >
                    {deptList.length === 0 ? (
                      <option value={ownerDept}>{ownerDept}</option>
                    ) : (
                      deptList.map((d) => (
                        <option key={d.id} value={d.name}>
                          {d.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Target Due Date *</label>
                  <input
                    type="date"
                    required
                    value={newActionDueDate}
                    onChange={(e) => setNewActionDueDate(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Assignee / Action Lead</label>
                <input
                  type="text"
                  value={newActionAssignee}
                  onChange={(e) => setNewActionAssignee(e.target.value)}
                  placeholder="e.g. Chief Flight Instructor / Maintenance Lead"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Detailed Description & Scope</label>
                <textarea
                  rows={3}
                  value={newActionDescription}
                  onChange={(e) => setNewActionDescription(e.target.value)}
                  placeholder="Outline the operational mitigation steps, SOP amendments, or training syllabus updates..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddActionModal(false)}
                  className="btn-secondary"
                  style={{ fontSize: '12px', padding: '7px 16px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingAction}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '7px 18px', backgroundColor: '#0284c7', borderColor: '#0369a1' }}
                >
                  {submittingAction ? 'Registering...' : 'Create Action (CAPA)'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Identify Safety Risk */}
      {showAddRiskModal && (
        <div className="modal-backdrop" style={{ zIndex: 10001 }}>
          <div className="modal-card" style={{ maxWidth: '600px', width: '100%', backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={18} color="#dc2626" />
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#0f172a' }}>
                  Identify Safety Risk for {indicatorDetail?.code}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddRiskModal(false)}
                className="modal-close-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateRisk} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                  Risk Headline / Precursor Threat *
                </label>
                <input
                  type="text"
                  required
                  value={newRiskTitle}
                  onChange={(e) => setNewRiskTitle(e.target.value)}
                  placeholder="e.g. Risk of runway veer-off during wet gusty landing operations"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              {/* 5x5 Matrix Parameters with live evaluation preview */}
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '8px', textTransform: 'uppercase' }}>
                  ICAO 5x5 Initial Risk Matrix Assessment
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-field">
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                      Severity (1 to 5)
                    </label>
                    <select
                      value={newRiskSeverity}
                      onChange={(e) => setNewRiskSeverity(Number(e.target.value))}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    >
                      <option value={5}>5 - Catastrophic (Loss of aircraft / fatalities)</option>
                      <option value={4}>4 - Major (Serious incident / structural damage)</option>
                      <option value={3}>3 - Moderate (Significant occurrence / injuries)</option>
                      <option value={2}>2 - Minor (Inconvenience / minor defect)</option>
                      <option value={1}>1 - Negligible (Insignificant operational impact)</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                      Likelihood (A to E)
                    </label>
                    <select
                      value={newRiskLikelihood}
                      onChange={(e) => setNewRiskLikelihood(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    >
                      <option value="A">A - Frequent (Likely to occur frequently)</option>
                      <option value="B">B - Occasional (Likely to occur sometimes)</option>
                      <option value="C">C - Remote (Unlikely, but possible)</option>
                      <option value="D">D - Improbable (Very unlikely to occur)</option>
                      <option value="E">E - Extremely Improbable (Almost inconceivable)</option>
                    </select>
                  </div>
                </div>

                {/* Live Matrix Index Preview */}
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Assessed Risk Index:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '14px', color: '#0f172a' }}>
                      {newRiskSeverity}{newRiskLikelihood}
                    </span>
                    <span className="badge-pill" style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      backgroundColor:
                        (newRiskSeverity === 5 && ['A', 'B', 'C'].includes(newRiskLikelihood)) || (newRiskSeverity === 4 && ['A', 'B'].includes(newRiskLikelihood)) || (newRiskSeverity === 3 && newRiskLikelihood === 'A')
                          ? '#fee2e2'
                          : (newRiskSeverity === 3 && newRiskLikelihood === 'E') || (newRiskSeverity === 2 && ['D', 'E'].includes(newRiskLikelihood)) || newRiskSeverity === 1
                          ? '#ecfdf5'
                          : '#fef3c7',
                      color:
                        (newRiskSeverity === 5 && ['A', 'B', 'C'].includes(newRiskLikelihood)) || (newRiskSeverity === 4 && ['A', 'B'].includes(newRiskLikelihood)) || (newRiskSeverity === 3 && newRiskLikelihood === 'A')
                          ? '#991b1b'
                          : (newRiskSeverity === 3 && newRiskLikelihood === 'E') || (newRiskSeverity === 2 && ['D', 'E'].includes(newRiskLikelihood)) || newRiskSeverity === 1
                          ? '#047857'
                          : '#92400e',
                    }}>
                      {(newRiskSeverity === 5 && ['A', 'B', 'C'].includes(newRiskLikelihood)) || (newRiskSeverity === 4 && ['A', 'B'].includes(newRiskLikelihood)) || (newRiskSeverity === 3 && newRiskLikelihood === 'A')
                        ? 'INTOLERABLE'
                        : (newRiskSeverity === 3 && newRiskLikelihood === 'E') || (newRiskSeverity === 2 && ['D', 'E'].includes(newRiskLikelihood)) || newRiskSeverity === 1
                        ? 'ACCEPTABLE'
                        : 'TOLERABLE'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Responsible Department</label>
                <select
                  value={newRiskDepartment || ownerDept}
                  onChange={(e) => setNewRiskDepartment(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                >
                  {deptList.length === 0 ? (
                    <option value={ownerDept}>{ownerDept}</option>
                  ) : (
                    deptList.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Description & Operational Context</label>
                <textarea
                  rows={2}
                  value={newRiskDescription}
                  onChange={(e) => setNewRiskDescription(e.target.value)}
                  placeholder="Detail the operational conditions, environment, or technical factors..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Assessment Justification</label>
                <textarea
                  rows={2}
                  value={newRiskJustification}
                  onChange={(e) => setNewRiskJustification(e.target.value)}
                  placeholder="Explain why this severity and likelihood was assessed based on current SPI trend rates..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddRiskModal(false)}
                  className="btn-secondary"
                  style={{ fontSize: '12px', padding: '7px 16px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingRisk}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '7px 18px', backgroundColor: '#dc2626', borderColor: '#b91c1c' }}
                >
                  {submittingRisk ? 'Registering...' : 'Identify & Link Risk'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User-friendly App Dialog Modal */}
      <AppDialogModal
        isOpen={dialogState.isOpen}
        type={dialogState.type}
        title={dialogState.title}
        message={dialogState.message}
        onClose={() => setDialogState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};
